import { useCallback, useEffect, useState } from 'react';
import {
  createSiteSupportRequest,
  loadSiteSupportRequests,
  updateSiteSupportRequest,
} from '../api/unionSupportApi.js';

const EMPTY = {
  viewerUserId: '',
  viewerName: '',
  viewerEmail: '',
  isPlatformAdmin: false,
  requests: [],
};

export function useSiteSupport({ manage = false } = {}) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });

  const refresh = useCallback(async ({ preserveNotice = false } = {}) => {
    setLoading(true);
    try {
      setData(await loadSiteSupportRequests({ manage }));
      if (!preserveNotice) setNotice({ type: '', message: '' });
    } catch (error) {
      setData(EMPTY);
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '사이트 문의를 불러오지 못했습니다.' });
    } finally {
      setLoading(false);
    }
  }, [manage]);

  useEffect(() => {
    let active = true;
    loadSiteSupportRequests({ manage })
      .then((result) => { if (active) setData(result); })
      .catch((error) => { if (active) setNotice({ type: 'error', message: error instanceof Error ? error.message : '사이트 문의를 불러오지 못했습니다.' }); })
      .finally(() => { if (active) setLoading(false); });
    const handleChange = () => { if (active) refresh({ preserveNotice: true }); };
    window.addEventListener('doroparty:site-support-changed', handleChange);
    return () => {
      active = false;
      window.removeEventListener('doroparty:site-support-changed', handleChange);
    };
  }, [manage, refresh]);

  const create = useCallback(async (input) => {
    if (working) return false;
    setWorking('create');
    setNotice({ type: '', message: '' });
    try {
      await createSiteSupportRequest(input);
      setData(await loadSiteSupportRequests({ manage: false }));
      setNotice({ type: 'success', message: '사이트 문의를 등록했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '사이트 문의를 등록하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [working]);

  const update = useCallback(async (input) => {
    if (working) return false;
    const id = String(input?.id || '');
    setWorking(`update:${id}`);
    setNotice({ type: '', message: '' });
    try {
      await updateSiteSupportRequest(input);
      setData(await loadSiteSupportRequests({ manage: true }));
      setNotice({ type: 'success', message: '사이트 문의 처리 내용을 저장했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '사이트 문의 처리 내용을 저장하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [working]);

  return { ...data, loading, working, notice, refresh, create, update };
}
